// Заголовок вкладки, описание и каноническая ссылка при переходах по сайту (изменение seo).
// Готовая страница приходит с ними от сервера; здесь они меняются, когда покупатель уходит
// на другую страницу без перезагрузки. Данные — только из кэша: хук сам ничего не запрашивает.
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useLocation } from "react-router";
import { getCatalog, getDocument, getDocuments } from "../api/endpoints";
import { CATALOG_KEY, DOCUMENTS_KEY } from "../app/queries";
import { SITE_DESCRIPTION, SITE_NAME, canonicalUrl, pageMetaFor, type DocumentsForMeta } from "./pageMeta";

const DOCUMENT_PATH = /^\/docs\/([^/]+)$/;

function setDescription(content: string) {
  let element = document.head.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (element === null) {
    element = document.createElement("meta");
    element.name = "description";
    document.head.append(element);
  }
  element.content = content;
}

function setCanonical(href: string | null) {
  const existing = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (href === null) {
    existing?.remove();
    return;
  }
  if (existing !== null) {
    existing.href = href;
    return;
  }
  const link = document.createElement("link");
  link.rel = "canonical";
  link.href = href;
  document.head.append(link);
}

export function usePageMeta() {
  const { pathname } = useLocation();
  const documentId = DOCUMENT_PATH.exec(pathname)?.[1] ?? "";
  const { data: catalog } = useQuery({ queryKey: CATALOG_KEY, queryFn: getCatalog, enabled: false });
  const { data: documentList } = useQuery({ queryKey: DOCUMENTS_KEY, queryFn: getDocuments, enabled: false });
  const { data: openDocument } = useQuery({
    queryKey: [...DOCUMENTS_KEY, documentId],
    queryFn: () => getDocument(documentId),
    enabled: false,
  });

  useEffect(() => {
    // Перешёл к документу по ссылке внутри сайта, а списка документов в кэше нет — хватит текста.
    const documents: DocumentsForMeta | undefined =
      documentList ?? (openDocument === undefined ? undefined : { documents: [openDocument] });
    const meta = pageMetaFor(pathname, catalog, documents);
    document.title = meta?.title ?? SITE_NAME;
    setDescription(meta?.description ?? SITE_DESCRIPTION);
    if (meta === null) {
      setCanonical(null);
    } else if (catalog?.site_url) {
      setCanonical(canonicalUrl(catalog.site_url, meta.path));
    }
  }, [pathname, catalog, documentList, openDocument]);
}
